import { useCallback, useEffect, useRef, useState } from 'react';
import { getPublicForm, submitPublicForm, uploadPublicFormFile } from '../core/api';
import { Logo } from '../shared/Logo';
import { useIsMobile } from '../shared/useMediaQuery';
import { fmtZoned } from '../core/utils';
import { INPUT, publicPayload, visibleFields } from './publicFormLogic';
import { SigningCard } from './PublicSigning';

/*
 * The page a form-link recipient sees — docs/tokenised-form-links-scoping-2026-09.md
 * Phase 1. Reached at `/f/<token>`, and it is the one screen in this app that
 * renders for somebody with no account.
 *
 * Five things about it are deliberate:
 *
 * 1. **It renders BEFORE the auth gate.** `App.jsx` returns `<LoginScreen>`
 *    for any unauthenticated visit, so without a branch above that check this
 *    page would be a login form — which for a garage with a link is a dead
 *    end, not a prompt.
 *
 * 2. **It sends no cookie** (`core/api.js`'s `credentials: 'omit'`). A
 *    recipient who also happens to be signed in here must not have their
 *    session ride along: the token is the entire authority, and mixing the
 *    two makes "what did this act as" ambiguous.
 *
 * 3. **It shows the org's own name**, from the server, and cannot be
 *    re-skinned. That is the first of §5.6's three answers to the fact that
 *    "update your bank details" is the most common invoice-fraud vector
 *    there is.
 *
 * 4. **Every server refusal renders the same way.** The backend answers one
 *    neutral 404 for unknown/expired/revoked/used/suspended, and this page
 *    must not try to be more helpful than that — inferring "probably
 *    expired" from a 404 would hand a prober exactly the distinction the
 *    backend refuses to make.
 *
 * 5. **Context fields are read-only and visibly so.** The recipient needs to
 *    know WHICH record they are filling in; letting them edit it would be a
 *    different feature with a different grant.
 *
 * Phase 2 adds one field type, `file` (§3.6) — "and attach the document" is
 * half of what the fleet cases in §1.2 actually want. `FileField` uploads
 * straight to GCS through a signed URL the backend mints per file and holds
 * only the returned object key as the field's value, which the recipient
 * never sees and could not usefully forge: the key is bound to this link and
 * this field, and the submit refuses one that is not. On mobile it offers
 * the camera as well as the file picker, the same `capture="environment"`
 * pair `live/RecordForm.jsx`'s own FileField uses — the document in question
 * is usually a piece of paper in the person's hand.
 */

export { INPUT };

const BTN = 'rounded-lg border border-sand dark:border-espresso-700 px-3 py-2 ' +
  'text-sm font-medium text-ink dark:text-paper disabled:opacity-60';

/* One `file` field. Uploads on selection rather than on submit — the whole
 * point of the signed-URL flow is that the bytes never pass through the
 * backend, so there is nothing to batch, and finding out at submit time that
 * a 12MB photo is too big would waste everything else the person typed.
 * Holds the object key as the field's value; shows the filename. */
function FileField({ token, spec, value, onChange, disabled }) {
  const pick = useRef(null);
  const camera = useRef(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [err, setErr] = useState(null);
  const isMobile = useIsMobile();

  const take = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';           // so re-picking the same file still fires
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const { object_key, filename } = await uploadPublicFormFile(token, spec.key, file);
      onChange(spec.key, object_key);
      setName(filename);
    } catch (e2) {
      // The server's own sentence: it is the only thing that knows whether
      // this was the wrong kind of file, too big, or too many.
      setErr(e2.message || "That file couldn't be attached.");
      onChange(spec.key, '');
      setName('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-w-0">
      <span className="block text-sm font-medium text-ink dark:text-paper mb-1">
        {spec.label || spec.key}
        {spec.required && <span className="text-rose-500 ml-0.5" aria-hidden="true">*</span>}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={BTN} disabled={disabled || busy}
                onClick={() => pick.current?.click()}>
          {busy ? 'Uploading…' : value ? 'Replace' : 'Choose file'}
        </button>
        {isMobile && (
          <button type="button" className={BTN} disabled={disabled || busy}
                  onClick={() => camera.current?.click()}>
            Take photo
          </button>
        )}
        {value && !busy && (
          <span className="text-sm text-pencil dark:text-sand min-w-0 truncate">
            {name || 'Attached'}
          </span>
        )}
      </div>
      <input ref={pick} type="file" accept="image/*,application/pdf"
             onChange={take} className="hidden" aria-label={spec.label || spec.key} />
      <input ref={camera} type="file" accept="image/*" capture="environment"
             onChange={take} className="hidden" />
      {err && <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{err}</p>}
      <p className="mt-1 text-xs text-pencil dark:text-sand">
        {spec.help || 'A photo or a PDF.'}
      </p>
    </div>
  );
}

// One question on a public form, with the control the record form would give it (WP-26.3): a yes/no is a checkbox, a rating is stars, a
// multi-choice is checkboxes (it used to be a single-choice dropdown), a date-time takes a time as well as a day. The VALUE it hands back is the
// type the server stores - a boolean, a star count, an array - so what the sender chose is what is saved. `reference` fields are not offered a
// picker: a form link's grant reads no other entity, so there is nothing it could honestly list; the value stays a pasted record id.
export function Field({ spec, value, onChange, disabled }) {
  const id = `f_${spec.key}`;
  const common = {
    id,
    value: value ?? '',
    disabled,
    onChange: (e) => onChange(spec.key, e.target.value),
    className: INPUT,
  };
  const labelText = (
    <>
      {spec.label || spec.key}
      {spec.required && <span className="text-rose-500 ml-0.5" aria-hidden="true">*</span>}
    </>
  );
  const help = spec.help && <p className="mt-1 text-xs text-pencil dark:text-sand">{spec.help}</p>;
  const labelCls = 'block text-sm font-medium text-ink dark:text-paper mb-1';

  if (spec.type === 'boolean') {
    return (
      <div className="min-w-0">
        <label htmlFor={id} className="flex items-center gap-2 text-sm font-medium text-ink dark:text-paper">
          <input id={id} type="checkbox" checked={value === true} disabled={disabled}
                 onChange={(e) => onChange(spec.key, e.target.checked)} className="h-4 w-4" />
          <span>{labelText}</span>
        </label>
        {help}
      </div>
    );
  }
  if (spec.type === 'rating') {
    const max = Number(spec.max) > 0 && Number(spec.max) <= 10 ? Number(spec.max) : 5;
    return (
      <div className="min-w-0" role="group" aria-labelledby={`${id}_label`}>
        <span id={`${id}_label`} className={labelCls}>{labelText}</span>
        <div className="flex gap-1">
          {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
            <button key={n} type="button" disabled={disabled} aria-label={`${n} of ${max}`} aria-pressed={Number(value) >= n}
                    onClick={() => onChange(spec.key, Number(value) === n ? '' : n)}
                    className={`text-2xl leading-none px-0.5 ${Number(value) >= n ? 'text-amber-500' : 'text-sand dark:text-espresso-700'}`}>
              ★
            </button>
          ))}
        </div>
        {help}
      </div>
    );
  }
  if (spec.type === 'multiselect' && Array.isArray(spec.options)) {
    const chosen = Array.isArray(value) ? value : [];
    return (
      <fieldset className="min-w-0" disabled={disabled}>
        <legend className={labelCls}>{labelText}</legend>
        <div className="space-y-1.5">
          {spec.options.map((o) => (
            <label key={o} className="flex items-center gap-2 text-sm text-ink dark:text-paper">
              <input type="checkbox" checked={chosen.includes(o)} className="h-4 w-4"
                     onChange={(e) => onChange(spec.key, e.target.checked ? [...chosen, o] : chosen.filter((x) => x !== o))} />
              {o}
            </label>
          ))}
        </div>
        {help}
      </fieldset>
    );
  }

  let control;
  if (spec.options) {
    control = (
      <select {...common}>
        <option value="">— choose —</option>
        {spec.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (spec.type === 'longtext') {
    control = <textarea {...common} rows={4} />;
  } else {
    const number = spec.type === 'number' || spec.type === 'money';
    const type = number ? 'number' : spec.type === 'datetime' ? 'datetime-local' : spec.type === 'date' ? 'date' : 'text';
    control = <input {...common} type={type} inputMode={number ? 'decimal' : undefined}
                     step={number ? (spec.step ?? 'any') : undefined} min={number ? spec.min : undefined} max={number ? spec.max : undefined} />;
  }
  return (
    <div className="min-w-0">
      <label htmlFor={id} className={labelCls}>{labelText}</label>
      {control}
      {help}
    </div>
  );
}

export function Shell({ children }) {
  return (
    <div className="min-h-screen bg-paper-50 dark:bg-espresso-950 px-4 py-8">
      <div className="mx-auto w-full max-w-lg">{children}</div>
    </div>
  );
}

export function Unavailable({ message }) {
  return (
    <Shell>
      <div className="rounded-2xl border border-sand dark:border-espresso-800 bg-paper dark:bg-espresso-900 p-6 text-center">
        <h1 className="font-display text-xl text-ink dark:text-paper">
          This link isn’t active
        </h1>
        {/* The server's own sentence, rendered verbatim. It is deliberately
            the same for every terminal state, and this page must not guess
            which one it was. */}
        <p className="mt-2 text-sm text-pencil dark:text-sand">{message}</p>
      </div>
    </Shell>
  );
}

export function PublicFormPage({ token }) {
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [signedDone, setSignedDone] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let live = true;
    getPublicForm(token)
      .then((f) => { if (live) setForm(f); })
      .catch((e) => { if (live) setError(e.message || 'This link is no longer active.'); });
    return () => { live = false; };
  }, [token]);

  const set = useCallback((k, v) => setValues((p) => ({ ...p, [k]: v })), []);

  const submit = async () => {
    setBusy(true);
    setSubmitError(null);
    try {
      await submitPublicForm(token, publicPayload(form.fields, values), name || undefined);
      setDone(true);
    } catch (e) {
      // A 404 here means the link died between load and submit (it is
      // single-use, so a second tab is the likely cause). Same neutral
      // message, same reason as everywhere else.
      setSubmitError(e.message || "That didn't save — mind trying again?");
    } finally {
      setBusy(false);
    }
  };

  // The record moved while they were reading it: fetch what is there now and ask them to look again. The link is still usable.
  const reloadAfterStale = async () => {
    try {
      const f = await getPublicForm(token);
      setForm(f);
      setNotice('The details changed while you were reading. What you see now is up to date. Please look it over again before you sign.');
    } catch (e) {
      setError(e.message || 'This link is no longer active.');
    }
  };

  if (error) return <Unavailable message={error} />;
  if (!form) {
    return (
      <Shell>
        <p className="text-center text-sm text-pencil dark:text-sand">Loading…</p>
      </Shell>
    );
  }

  if (signedDone) {
    return (
      <Shell>
        <div className="rounded-2xl border border-sand dark:border-espresso-800 bg-paper dark:bg-espresso-900 p-6 text-center">
          <h1 className="font-display text-xl text-ink dark:text-paper">Thank you — your signature has been recorded</h1>
          <p className="mt-2 text-sm text-pencil dark:text-sand">
            {form.org_name} has it. This link has now been used, so it won’t open again.
          </p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="rounded-2xl border border-sand dark:border-espresso-800 bg-paper dark:bg-espresso-900 p-6 text-center">
          <h1 className="font-display text-xl text-ink dark:text-paper">Thank you</h1>
          <p className="mt-2 text-sm text-pencil dark:text-sand">
            {form.org_name} has your answer. This link has now been used, so
            it won’t open again.
          </p>
        </div>
      </Shell>
    );
  }

  const contextKeys = Object.keys(form.context || {});

  if (form.sign) {
    return (
      <Shell>
        <div className="flex items-center gap-2 mb-5 min-w-0">
          <Logo size={22} />
          <span className="font-display font-semibold text-ink dark:text-paper truncate">{form.org_name}</span>
        </div>
        <div className="rounded-2xl border border-sand dark:border-espresso-800 bg-paper dark:bg-espresso-900 p-5 md:p-6">
          <SigningCard token={token} form={form} notice={notice} onSigned={() => setSignedDone(true)}
                       onStale={reloadAfterStale} onDead={(m) => setError(m || 'This link is no longer active.')} />
          <p className="mt-3 text-xs text-pencil dark:text-sand text-center">
            You can use this link once. It expires {fmtZoned(form.expires_at)}.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex items-center gap-2 mb-5 min-w-0">
        <Logo size={22} />
        {/* The ORG's name, from the server. See this file's header on why. */}
        <span className="font-display font-semibold text-ink dark:text-paper truncate">
          {form.org_name}
        </span>
      </div>

      <div className="rounded-2xl border border-sand dark:border-espresso-800 bg-paper dark:bg-espresso-900 p-5 md:p-6">
        <h1 className="font-display text-lg text-ink dark:text-paper">
          {form.op === 'create' ? `New ${form.entity_label}` : form.entity_label}
        </h1>

        {contextKeys.length > 0 && (
          <dl className="mt-3 rounded-lg bg-paper-50 dark:bg-espresso-950 px-3 py-2.5 text-sm">
            {contextKeys.map((k) => (
              <div key={k} className="flex flex-wrap gap-x-2 py-0.5">
                <dt className="text-pencil dark:text-sand">
                  {(form.context_labels || {})[k] || k}
                </dt>
                <dd className="text-ink dark:text-paper min-w-0 wrap-break-word">
                  {String(form.context[k] ?? '—')}
                </dd>
              </div>
            ))}
          </dl>
        )}

        <div className="mt-5 space-y-4">
          {visibleFields(form.fields, values).map((spec) => (spec.type === 'file' ? (
            <FileField key={spec.key} token={token} spec={spec}
                       value={values[spec.key]} onChange={set} disabled={busy} />
          ) : (
            <Field key={spec.key} spec={spec} value={values[spec.key]}
                   onChange={set} disabled={busy} />
          )))}

          <div className="min-w-0">
            <label htmlFor="f_name"
                   className="block text-sm font-medium text-ink dark:text-paper mb-1">
              Your name <span className="text-pencil dark:text-sand font-normal">(optional)</span>
            </label>
            <input id="f_name" value={name} disabled={busy} className={INPUT}
                   onChange={(e) => setName(e.target.value)} />
            <p className="mt-1 text-xs text-pencil dark:text-sand">
              So they know who filled this in.
            </p>
          </div>
        </div>

        {submitError && (
          <p className="mt-4 text-sm text-rose-600 dark:text-rose-400">{submitError}</p>
        )}

        <button onClick={submit} disabled={busy}
                className="mt-5 w-full rounded-lg bg-sage-600 hover:bg-sage-700
                           disabled:opacity-60 px-4 py-2.5 text-base font-medium text-white
                           transition-colors">
          {busy ? 'Sending…' : 'Submit'}
        </button>

        <p className="mt-3 text-xs text-pencil dark:text-sand text-center">
          You can use this link once. It expires{' '}
          {fmtZoned(form.expires_at)}.
        </p>
      </div>
    </Shell>
  );
}
